import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const ROLE_LABELS = {
  superadmin:     'Super Admin',
  security_head:  'Security Head',
  admin_head:     'Admin Head',
  location_admin: 'Location Admin',
  guard:          'Reception',
  employee:       'Employee',
};

const Navbar = () => {
  const { user, logout, hasRole, hasMinRole, canViewAllOffices } = useAuth();
  const navigate  = useNavigate();
  const { pathname } = useLocation();

  const handleLogout = () => { logout(); navigate('/login'); };

  const collapseNav = (callback) => {
    const nav = document.getElementById('navbarNav');
    if (nav?.classList.contains('show')) {
      nav.classList.remove('show');
      if (callback) setTimeout(callback, 300);
    } else {
      if (callback) callback();
    }
  };

  const navLink = (to, label, icon) => (
    <li className="nav-item" key={to}>
      <Link
        className={`nav-link d-flex align-items-center gap-1 ${pathname === to ? 'active fw-semibold' : ''}`}
        to={to}
        onClick={(e) => { e.preventDefault(); collapseNav(() => navigate(to)); }}
        style={{
          color:      pathname === to ? '#fff' : 'rgba(255,255,255,0.88)',
          background: pathname === to ? 'rgba(255,255,255,0.18)' : 'transparent',
          borderRadius: 8,
          padding: '6px 14px',
          fontSize: '0.86rem',
          fontWeight: pathname === to ? 600 : 400,
          transition: 'all 0.15s',
        }}
      >
        <i className={`bi ${icon}`} style={{ fontSize: '0.9rem' }}></i>
        <span>{label}</span>
      </Link>
    </li>
  );

  /* ── Simplified navbar for guard/reception (kiosk mode) ── */
  if (hasRole('guard')) {
    return (
      <nav className="navbar sticky-top shadow"
        style={{ background: 'linear-gradient(90deg,#065a30,#086F3C)', zIndex: 1030, minHeight: 64, borderBottom: '1px solid rgba(255,255,255,0.15)' }}>
        <div className="container-fluid px-3 px-lg-4 d-flex align-items-center">
          {/* Brand */}
          <Link to="/" className="navbar-brand d-flex align-items-center gap-3 text-decoration-none me-3">
            <div style={{ background: '#fff', borderRadius: 12, padding: '5px 10px', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 8px rgba(0,0,0,0.15)' }}>
              <img src="/think-gas-logo.png" alt="Think Gas" style={{ height: 36, width: 36, objectFit: 'contain', display: 'block' }} />
            </div>
            <div style={{ lineHeight: 1.25 }}>
              <div style={{ fontSize: '1.05rem', fontWeight: 800, color: '#fff', letterSpacing: '0.06em', textShadow: '0 1px 3px rgba(0,0,0,0.2)' }}>THINK GAS</div>
              <div style={{ fontSize: '0.5rem', color: 'rgba(255,255,255,0.9)', letterSpacing: '0.16em', fontWeight: 500, textTransform: 'uppercase' }}>Visitor Registration</div>
            </div>
          </Link>
          {/* Office badge */}
          {user?.office && (
            <div className="d-flex align-items-center gap-2 px-3 py-1 rounded-3"
              style={{ background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.25)' }}>
              <i className="bi bi-geo-alt-fill text-warning" style={{ fontSize: '0.85rem' }}></i>
              <div style={{ lineHeight: 1.2 }}>
                <div style={{ fontSize: '0.82rem', fontWeight: 700, color: '#fff' }}>{user.office.name}</div>
                <div style={{ fontSize: '0.62rem', color: 'rgba(255,255,255,0.75)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{user.office.city} · {user.office.code}</div>
              </div>
            </div>
          )}
          {/* Subtle logout — icon only, right-most */}
          <button
            onClick={handleLogout}
            title="Logout"
            style={{ marginLeft: 'auto', background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', color: 'rgba(255,255,255,0.7)', borderRadius: 8, padding: '8px 12px', cursor: 'pointer', transition: 'all 0.15s' }}
            onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.25)'}
            onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.1)'}>
            <i className="bi bi-box-arrow-right" style={{ fontSize: '1rem' }}></i>
          </button>
        </div>
      </nav>
    );
  }

  return (
    <nav
      className="navbar navbar-expand-lg sticky-top shadow"
      style={{
        background: 'linear-gradient(90deg, #065a30 0%, #086F3C 40%, #065a30 100%)',
        zIndex: 1030,
        borderBottom: '1px solid rgba(255,255,255,0.15)',
        minHeight: 64,
      }}
    >
      <div className="container-fluid px-3 px-lg-4">

        {/* Brand */}
        <Link
          className="navbar-brand d-flex align-items-center gap-3 text-decoration-none"
          to="/"
          onClick={(e) => { e.preventDefault(); collapseNav(() => navigate('/')); }}
          style={{ padding: '6px 0' }}
        >
          <div style={{
            background: '#fff', borderRadius: 12, padding: '5px 10px',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)', flexShrink: 0,
          }}>
            <img src="/think-gas-logo.png" alt="Think Gas" style={{ height: 36, width: 36, objectFit: 'contain', display: 'block' }} />
          </div>
          <div style={{ lineHeight: 1.25 }}>
            <div style={{ fontSize: '1.05rem', fontWeight: 800, color: '#fff', letterSpacing: '0.06em', textShadow: '0 1px 3px rgba(0,0,0,0.2)' }}>
              THINK GAS
            </div>
            <div style={{ fontSize: '0.5rem', color: 'rgba(255,255,255,0.9)', letterSpacing: '0.16em', fontWeight: 500, textTransform: 'uppercase' }}>
              Visitor Entry Portal
            </div>
          </div>
        </Link>

        {/* Office badge — shown for location_admin and guard */}
        {user?.office && !canViewAllOffices() && (
          <div
            className="d-none d-lg-flex align-items-center gap-2 ms-3 px-3 py-1 rounded-3"
            style={{ background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.25)' }}
          >
            <i className="bi bi-geo-alt-fill text-warning" style={{ fontSize: '0.8rem' }}></i>
            <div style={{ lineHeight: 1.2 }}>
              <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#fff' }}>{user.office.name}</div>
              <div style={{ fontSize: '0.58rem', color: 'rgba(255,255,255,0.7)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{user.office.city} · {user.office.code}</div>
            </div>
          </div>
        )}

        {/* "All Offices" badge for global viewers */}
        {canViewAllOffices() && (
          <div
            className="d-none d-lg-flex align-items-center gap-2 ms-3 px-3 py-1 rounded-3"
            style={{ background: 'rgba(255,200,0,0.18)', border: '1px solid rgba(255,200,0,0.35)' }}
          >
            <i className="bi bi-globe2" style={{ fontSize: '0.8rem', color: '#ffd700' }}></i>
            <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#ffd700' }}>All Offices</div>
          </div>
        )}

        <div className="d-none d-lg-block mx-3" style={{ width: 1, height: 32, background: 'rgba(255,255,255,0.25)' }} />

        <button className="navbar-toggler border-0 shadow-none p-1" type="button" data-bs-toggle="collapse" data-bs-target="#navbarNav">
          <span className="navbar-toggler-icon" style={{ filter: 'brightness(0) invert(1)' }}></span>
        </button>

        <div className="collapse navbar-collapse" id="navbarNav">
          <ul className="navbar-nav me-auto mb-0 d-flex align-items-lg-center gap-1 py-2 py-lg-0">
            {!hasRole('guard') && navLink('/', 'Dashboard', 'bi-speedometer2')}
            {hasRole('guard') && navLink('/register', 'Register Visitor', 'bi-person-plus-fill')}
            {hasRole('guard') && navLink('/records', 'Records', 'bi-table')}
            {!hasRole('guard') && hasRole('superadmin') && navLink('/register', 'Register Visitor', 'bi-person-plus-fill')}
            {!hasRole('guard') && navLink('/records', 'Records', 'bi-table')}
            {!hasRole('guard') && navLink('/reports', 'Reports', 'bi-bar-chart-fill')}
            {hasMinRole('location_admin') && navLink('/users', 'Users', 'bi-people-fill')}
            {hasRole('superadmin') && navLink('/offices', 'Offices', 'bi-buildings-fill')}
          </ul>

          <div className="d-flex align-items-center gap-2 mt-2 mt-lg-0">
            {/* Kiosk link for location_admin only */}
            {user?.office && hasRole('location_admin') && (
              <a
                href={`/kiosk?office=${user.office.code}`}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-sm d-flex align-items-center gap-1"
                style={{ background: 'rgba(255,165,0,0.2)', color: '#ffd700', border: '1px solid rgba(255,165,0,0.5)', borderRadius: 8, padding: '6px 12px', fontSize: '0.82rem' }}
              >
                <i className="bi bi-tablet"></i>
                <span className="d-none d-lg-inline">Kiosk</span>
              </a>
            )}

            {/* User pill */}
            <div
              className="d-none d-lg-flex align-items-center gap-2 px-3 py-2 rounded-3"
              style={{ background: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(4px)' }}
            >
              <div
                className="rounded-circle d-flex align-items-center justify-content-center fw-bold text-white flex-shrink-0"
                style={{ width: 30, height: 30, background: 'rgba(255,255,255,0.25)', fontSize: '0.8rem', border: '1.5px solid rgba(255,255,255,0.4)' }}
              >
                {user?.name?.[0]?.toUpperCase()}
              </div>
              <div style={{ lineHeight: 1.2 }}>
                <div style={{ fontSize: '0.8rem', fontWeight: 600, color: '#fff' }}>{user?.name}</div>
                <div style={{ fontSize: '0.62rem', color: 'rgba(255,255,255,0.8)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  {ROLE_LABELS[user?.role] || user?.role}
                </div>
              </div>
            </div>

            <button
              className="btn btn-sm fw-semibold d-flex align-items-center gap-2"
              style={{ background: 'rgba(255,255,255,0.15)', color: '#fff', border: '1.5px solid rgba(255,255,255,0.4)', borderRadius: 8, padding: '6px 14px', backdropFilter: 'blur(4px)', transition: 'all 0.15s' }}
              onClick={() => { collapseNav(); handleLogout(); }}
              onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.28)'}
              onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.15)'}
            >
              <i className="bi bi-box-arrow-right"></i>
              <span className="d-none d-lg-inline">Logout</span>
            </button>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
